import {
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantProvider from "src/providers/merchant-provider";
import type {
	MerchantNotification,
	MerchantNotificationUpsertRequest,
	NotificationTestResult,
} from "src/types";

const requireMerchantId = (merchantId: string | null): string => {
	if (!merchantId) {
		throw new Error("Merchant ID is required");
	}
	return merchantId;
};

const notificationQueries = {
	listNotifications: (): UseQueryResult<MerchantNotification[]> => {
		const { merchantId } = useActiveMerchantId();

		return useQuery<MerchantNotification[]>({
			queryKey: ["listNotifications", merchantId],
			queryFn: () =>
				merchantProvider.listMerchantNotifications(
					requireMerchantId(merchantId),
				),
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	upsertNotification: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: ({
				channel,
				params,
			}: {
				channel: string;
				params: MerchantNotificationUpsertRequest;
			}) =>
				merchantProvider.upsertMerchantNotification(
					requireMerchantId(merchantId),
					channel,
					params,
				),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ["listNotifications", merchantId],
				});
			},
		});
	},

	testNotification: () => {
		const { merchantId } = useActiveMerchantId();

		return useMutation<NotificationTestResult, Error, string>({
			mutationFn: (channel: string) =>
				merchantProvider.testMerchantNotification(
					requireMerchantId(merchantId),
					channel,
				),
		});
	},

	deleteNotification: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation<void, Error, string>({
			mutationFn: (channel: string) =>
				merchantProvider.deleteMerchantNotification(
					requireMerchantId(merchantId),
					channel,
				),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ["listNotifications", merchantId],
				});
			},
		});
	},
};

export default notificationQueries;
